import { OptionRelatedInstituteSettings, ProctoringOption, ProctoringPlatforms } from "@verity/proctoring-option/types";
import { ReactNode, useEffect, useId, useRef, useState } from "react";
import {
	getProctoringPlatformName,
	PROCTORING_OPTION_TEXT,
	ProctoringOptionCategory,
} from "../proctoring-options/option-text/ProctoringOptionText";
import {
	DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	getSupportedOptionValue,
	isOptionSupportedForPlatform,
} from "@verity/proctoring-option";
import i18nPackage from "../../i18n/i18n";
import {
	CoreCheckbox,
	CoreChip,
	CoreInfoIndicator,
	CoreNoResults,
	CoreRadioCard,
	CoreRadioCardIcon,
	CoreRadioCardSelectIcon,
} from "../../core";
import { Divider, Link, Typography } from "@mui/material";
import styles from "./PlatformStep.module.css";
import { Trans } from "react-i18next";
import UnsupportedModal from "../edit-quiz-modals/unsupportedModal";
import StepDescription, { StepWrapper } from "./CommonStepComponents";
import ExtensionIcon from "../images/platform/extension.svg?react";
import LockdownIcon from "../images/platform/lockdown.svg?react";
import ChromeIcon from "../images/platform/chrome.svg?react";
import EdgeIcon from "../images/platform/edge.svg?react";
import BraveIcon from "../images/platform/brave.svg?react";
import OperaIcon from "../images/platform/opera.svg?react";
import FirefoxIcon from "../images/platform/firefox.svg?react";
import SafariIcon from "../images/platform/safari.svg?react";
import WindowsIcon from "../images/platform/windows.svg?react";
import MacIcon from "../images/platform/mac.svg?react";
import { CoreLoadingPage } from "../../core/progress-indicators/Spinner";
import { VerityInstituteSettings } from "../../types/common";
import { EditQuizPageProps } from "../editQuiz";

export default function PlatformStep({ forceChromeOsManagedGuestSession = false, ...props }: ModeOptionsProps) {
	return (
		<StepWrapper>
			<div className={styles.platformSection}>
				<ModeOptions forceChromeOsManagedGuestSession={forceChromeOsManagedGuestSession} {...props} />
			</div>
		</StepWrapper>
	);
}

type ModeOptionsProps = {
	forceChromeOsManagedGuestSession?: boolean;
	loading?: boolean;
	isInstitutionProfile?: boolean;
	optionRelatedInstituteSettings?: OptionRelatedInstituteSettings;
	verityInstituteSettings: VerityInstituteSettings;
} & Required<Pick<EditQuizPageProps, "enforcedProfilesOnly" | "setProctoringOption" | "proctoringOption">>;

export function ModeOptions({
	forceChromeOsManagedGuestSession = false,
	loading,
	isInstitutionProfile = false,
	enforcedProfilesOnly,
	setProctoringOption,
	verityInstituteSettings,
	optionRelatedInstituteSettings = DEFAULT_OPTION_RELATED_INSTITUTE_SETTINGS,
	proctoringOption: option,
}: ModeOptionsProps) {
	const [isExtensionEnabled, setIsExtensionEnabled] = useState<boolean>(
		Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform),
	);
	const [isMobileEnabled, setIsMobileEnabled] = useState<boolean>(
		Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform),
	);
	const [isLockdownEnabled, setIsLockdownEnabled] = useState<boolean>(
		Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform),
	);
	const [isChromeOsEnabled, setIsChromeOsEnabled] = useState<boolean>(Boolean(!isLockdownEnabled || option.isChromeOs));
	const [newOptions, setNewOptions] = useState<typeof option>();
	const [platformUnsupportedOptionNames, setPlatformUnsupportedOptionNames] = useState<{
		[P in keyof ProctoringPlatforms | "none"]?: {
			[P in ProctoringOptionCategory]?: Array<{
				name: string;
				option: keyof ProctoringOption;
			}>;
		};
	}>();
	const [unsupportedModalOpen, setUnsupportedModalOpen] = useState(false);
	const initialLockdownMode = useRef(Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform));
	const initialOneScreenEnforcement = useRef(option.oneScreenEnforcementMode);

	useEffect(() => {
		const extensionChange = Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform);
		const mobileChange = Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform);
		const lockdownChange = Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform);
		const chromeOsChange = Boolean(extensionChange || option.isChromeOs);
		if (isExtensionEnabled !== extensionChange) {
			setIsExtensionEnabled(extensionChange);
		}
		if (isMobileEnabled !== mobileChange) {
			setIsMobileEnabled(mobileChange);
		}
		if (isLockdownEnabled !== lockdownChange) {
			setIsLockdownEnabled(lockdownChange);
		}
		if (isChromeOsEnabled !== chromeOsChange) {
			setIsChromeOsEnabled(chromeOsChange);
		}
	}, [option, verityInstituteSettings]);

	useEffect(() => {
		const unsupportedOptionNames: typeof platformUnsupportedOptionNames = {};
		const newOption = { ...option };

		newOption.isExtension = isExtensionEnabled;
		newOption.isLockdown = isLockdownEnabled;
		newOption.isMobile = isMobileEnabled;
		newOption.isChromeOs = isChromeOsEnabled;

		if (!initialLockdownMode.current) {
			if (isLockdownEnabled && !isInstitutionProfile) {
				newOption.oneScreenEnforcementMode = "normal";
			} else {
				newOption.oneScreenEnforcementMode = initialOneScreenEnforcement.current;
			}
		}

		// do not show warning modal if platform options did not change
		const showModal =
			newOption.isExtension !== Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform) ||
			newOption.isMobile !== Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform) ||
			newOption.isLockdown !== Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform);

		const activePlatformKeys: (keyof ProctoringPlatforms)[] = [];
		if (isExtensionEnabled) activePlatformKeys.push("isExtension");
		if (isLockdownEnabled) activePlatformKeys.push("isLockdown");
		if (isMobileEnabled) activePlatformKeys.push("isMobile");

		Object.keys(newOption).forEach((k) => {
			const key = k as keyof ProctoringOption;
			const value = newOption[key];
			if (value !== undefined) {
				const supportedValue = getSupportedOptionValue(newOption, key, value);

				// Check if the other selected platforms support this option
				const isSupportedByAlternative = activePlatformKeys.some((p) => isOptionSupportedForPlatform(p, key, value));

				if (value !== supportedValue.value && !isSupportedByAlternative) {
					// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
					(newOption[key] as typeof supportedValue.value) = supportedValue.value;

					if (showModal) {
						const optionHeader = PROCTORING_OPTION_TEXT[key]?.header;
						const optionCategory = PROCTORING_OPTION_TEXT[key]?.category;
						if (optionHeader && optionCategory) {
							const text = `${optionHeader} ${getOptionModifierText(supportedValue.value, option[key])}`;
							const unsupportedOptionNamesEntry = {
								name: text,
								option: key,
							};
							const platform = supportedValue.platform ?? "none";
							if (!unsupportedOptionNames[platform]) {
								unsupportedOptionNames[platform] = { [optionCategory]: [] };
							} else if (!unsupportedOptionNames[platform]?.[optionCategory]) {
								unsupportedOptionNames[platform] = {
									...unsupportedOptionNames[platform],
									[optionCategory]: [],
								};
							}
							unsupportedOptionNames[platform]?.[optionCategory]?.push(unsupportedOptionNamesEntry);
						}
					}
				}
			}
		});

		if (showModal && Object.keys(unsupportedOptionNames).length > 0) {
			setPlatformUnsupportedOptionNames(unsupportedOptionNames);
			setUnsupportedModalOpen(true);
			setNewOptions(newOption);
		} else {
			setProctoringOption(newOption);
		}
	}, [isExtensionEnabled, isMobileEnabled, isLockdownEnabled, isChromeOsEnabled]);

	const handleExtensionChange = () => {
		if (isExtensionEnabled && isLockdownEnabled) {
			setIsExtensionEnabled(false);
		} else {
			setIsExtensionEnabled((prev) => {
				if (!prev) {
					setIsLockdownEnabled(false);
					setIsChromeOsEnabled(true);
				}
				return true;
			});
		}
	};

	const handleLockdownChange = () => {
		if (isExtensionEnabled && isLockdownEnabled) {
			setIsLockdownEnabled(false);
		} else {
			setIsLockdownEnabled((prev) => {
				if (!prev) setIsExtensionEnabled(false);
				return true;
			});
		}
	};

	const onCloseUnsupportedModal = () => {
		setPlatformUnsupportedOptionNames(undefined);
		setNewOptions(undefined);
		setUnsupportedModalOpen(false);

		const newIsExtensionEnabled = Boolean(option.isExtension && verityInstituteSettings.allowExtensionPlatform);
		setIsExtensionEnabled(newIsExtensionEnabled);
		setIsLockdownEnabled(Boolean(option.isLockdown && verityInstituteSettings.allowLockdownPlatform));
		setIsMobileEnabled(Boolean(option.isMobile && verityInstituteSettings.allowMobilePlatform));
		setIsChromeOsEnabled(Boolean(newIsExtensionEnabled || option.isChromeOs));
	};
	// Declare separately to make linter happy
	const instructionLinkAriaLabel = i18nPackage.t("editQuizPage.platformStep.ariaLabel");

	// This step will not be shown if enforced profile
	if (enforcedProfilesOnly) return null;

	const noPlatformsEnabled =
		!verityInstituteSettings.allowExtensionPlatform && !verityInstituteSettings.allowLockdownPlatform;

	if (loading) return <CoreLoadingPage size={100} />;

	if (noPlatformsEnabled) {
		return (
			<CoreNoResults
				variant="no-results-variant"
				headerVariant="h2"
				header={i18nPackage.t("editQuizPage.platformStep.allDisabled")}
			/>
		);
	}

	return (
		<>
			<StepDescription>
				<Trans
					i18n={i18nPackage}
					i18nKey={"editQuizPage.platformStep.description"}
					components={{
						a: (
							<Link
								href="https://support.yuja.com/hc/en-us/articles/24085929932567-YuJa-Verity-Test-Proctoring-Overview#01JFTX1PEYZH76PBSADCB21J6C"
								target="_blank"
								rel="noreferrer"
								aria-label={instructionLinkAriaLabel}
							/>
						),
					}}
				/>
			</StepDescription>
			<div className={styles.proctorSettingsGridPlatforms}>
				{verityInstituteSettings.allowExtensionPlatform && (
					<ExtensionButton onClick={handleExtensionChange} checked={isExtensionEnabled}>
						{verityInstituteSettings.allowMobilePlatform && isExtensionEnabled && (
							<MobileCheckBox isMobileEnabled={isMobileEnabled} setIsMobileEnabled={setIsMobileEnabled} />
						)}
					</ExtensionButton>
				)}
				{verityInstituteSettings.allowLockdownPlatform && (
					<LockdownButton onClick={handleLockdownChange} checked={isLockdownEnabled}>
						<div className={styles.checkboxes}>
							{verityInstituteSettings.allowMobilePlatform && isLockdownEnabled && (
								<MobileCheckBox isMobileEnabled={isMobileEnabled} setIsMobileEnabled={setIsMobileEnabled} />
							)}
							{isLockdownEnabled && !forceChromeOsManagedGuestSession && (
								<ChromeOsCheckbox
									checked={isChromeOsEnabled}
									onChange={setIsChromeOsEnabled}
									lockdownRequireManagedGuestSession={optionRelatedInstituteSettings.lockdownRequireManagedGuestSession}
								/>
							)}
						</div>
					</LockdownButton>
				)}
			</div>
			{unsupportedModalOpen && platformUnsupportedOptionNames && newOptions && (
				<UnsupportedModal
					open={unsupportedModalOpen}
					onClose={onCloseUnsupportedModal}
					onConfirm={() => {
						setProctoringOption(newOptions);
						setPlatformUnsupportedOptionNames(undefined);
						setNewOptions(undefined);
						setUnsupportedModalOpen(false);
					}}
					platformUnsupportedOptionNames={platformUnsupportedOptionNames}
				/>
			)}
		</>
	);
}

function getOptionModifierText<T extends Partial<ProctoringOption>>(
	newValue: T[keyof T],
	oldValue: T[keyof T],
): string {
	if (newValue === "tab" && oldValue === "system") {
		return i18nPackage.t("editQuizPage.platformStep.system");
	}
	if ((typeof newValue === "string" && newValue !== "off" && oldValue === "off") || newValue === true) {
		return i18nPackage.t("editQuizPage.platformStep.disabled");
	}
	return "";
}

function SupportedBrowsers() {
	return (
		<div className={styles.badgeRow}>
			<CoreChip className={styles.platformBadge} chipColor="#E8F5E9" sx={{ color: "#2E7D32" }} icon={<ChromeIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.extension.chrome")}
			</CoreChip>
			<CoreChip className={styles.platformBadge} chipColor="#E3F2FD" sx={{ color: "#1565C0" }} icon={<EdgeIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.extension.edge")}
			</CoreChip>
			<CoreChip className={styles.platformBadge} chipColor="#FFF3E0" sx={{ color: "#E65100" }} icon={<BraveIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.extension.brave")}
			</CoreChip>
			<CoreChip className={styles.platformBadge} chipColor="#FCE4EC" sx={{ color: "#C2185B" }} icon={<OperaIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.extension.opera")}
			</CoreChip>
			<CoreChip className={styles.platformBadge} chipColor="#FFEBEE" sx={{ color: "#C62828" }} icon={<FirefoxIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.extension.firefox")}
			</CoreChip>
			<CoreChip className={styles.platformBadge} chipColor="#E1F5FE" sx={{ color: "#0277BD" }} icon={<SafariIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.extension.safari")}
			</CoreChip>
		</div>
	);
}

function SupportedOS() {
	return (
		<div className={styles.badgeRow}>
			<CoreChip className={styles.platformBadge} chipColor="#E3F2FD" sx={{ color: "#1565C0" }} icon={<WindowsIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.lockdown.windows")}
			</CoreChip>
			<CoreChip className={styles.platformBadge} chipColor="#F3F4F6" sx={{ color: "#374151" }} icon={<MacIcon />}>
				{i18nPackage.t("editQuizPage.platformStep.lockdown.macOs")}
			</CoreChip>
		</div>
	);
}

type EditQuizButtonProps = {
	onClick: () => void;
	checked: boolean;
	title: string;
	description: React.ReactNode;
	subtitle: string;
	icon: React.ReactNode;
	supportedComponent: React.ReactNode;
	children?: React.ReactNode;
};

function PlatformButton({
	checked,
	onClick,
	description,
	icon,
	subtitle,
	supportedComponent,
	title,
	children,
}: EditQuizButtonProps) {
	return (
		<CoreRadioCard
			selected={checked}
			onClick={onClick}
			slotProps={{
				container: { className: styles.platformButtonWrapper },
				button: { className: styles.platformButton },
				actionWrapper: { className: styles.contentWrapper },
			}}
			secondaryAction={
				children && checked ? (
					<>
						<Divider className={styles.platformDivider} />
						{children}
					</>
				) : null
			}
		>
			<CoreRadioCardIcon enabled={checked} icon={icon} className={styles.platformIcon} />
			<div className={styles.platformInfoWrapper}>
				<div className={styles.platformColWrapper}>
					<div className={styles.platformTitleCheckmarkWrapper}>
						<Typography variant="h3" className={styles.title}>
							{title}
						</Typography>
						<CoreRadioCardSelectIcon selected={checked} />
					</div>
					<Typography className={styles.description}>{description}</Typography>
				</div>
				<div className={styles.platformColWrapper}>
					<Typography className={styles.subtitle}>{subtitle}</Typography>
					{supportedComponent}
				</div>
			</div>
		</CoreRadioCard>
	);
}

function LockdownButton({ checked, onClick, children }: Pick<EditQuizButtonProps, "children" | "checked" | "onClick">) {
	return (
		<PlatformButton
			title={getProctoringPlatformName("lockdown")}
			icon={<LockdownIcon />}
			description={
				<Trans
					i18n={i18nPackage}
					i18nKey={"editQuizPage.platformStep.lockdown.description"}
					components={{ b: <strong /> }}
				/>
			}
			subtitle={i18nPackage.t("editQuizPage.platformStep.lockdown.subtitle")}
			supportedComponent={<SupportedOS />}
			checked={checked}
			onClick={onClick}
		>
			{children}
		</PlatformButton>
	);
}

function ExtensionButton({
	checked,
	onClick,
	children,
}: Pick<EditQuizButtonProps, "children" | "checked" | "onClick">) {
	return (
		<PlatformButton
			title={getProctoringPlatformName("extension")}
			icon={<ExtensionIcon />}
			description={
				<Trans
					i18n={i18nPackage}
					i18nKey={"editQuizPage.platformStep.extension.description"}
					components={{ b: <strong /> }}
				/>
			}
			subtitle={i18nPackage.t("editQuizPage.platformStep.extension.subtitle")}
			supportedComponent={<SupportedBrowsers />}
			checked={checked}
			onClick={onClick}
		>
			{children}
		</PlatformButton>
	);
}

function LabeledCheckbox({
	checked,
	onChange,
	header,
	hint,
	children,
}: {
	checked: boolean;
	onChange: (value: boolean) => void;
	header: ReactNode;
	hint?: ReactNode;
	children: ReactNode;
}) {
	const id = useId();
	return (
		<div className={styles.labeledCheckboxContainer}>
			<CoreCheckbox size="17px" onClick={() => onChange(!checked)} checked={checked} id={id} />

			<label className={styles.checkboxInfoWrapper} htmlFor={id}>
				<div className={styles.checkboxHeaderWrapper}>
					<Typography component={"span"} className={styles.protoredMethodTitleSub}>
						{header}
					</Typography>
					{hint && <CoreInfoIndicator hint={hint} size={20} />}
				</div>
				<Typography component="span" className={styles.protoredMethodDescriptionSub}>
					{children}
				</Typography>
			</label>
		</div>
	);
}

function MobileCheckBox({
	setIsMobileEnabled,
	isMobileEnabled,
}: {
	isMobileEnabled: boolean;
	setIsMobileEnabled: (value: boolean) => void;
}) {
	return (
		<LabeledCheckbox
			checked={isMobileEnabled}
			onChange={setIsMobileEnabled}
			header={i18nPackage.t("editQuizPage.platformStep.mobile.title")}
			hint={i18nPackage.t("editQuizPage.platformStep.mobile.hint")}
		>
			{i18nPackage.t("editQuizPage.platformStep.mobile.description")}
		</LabeledCheckbox>
	);
}

function ChromeOsCheckbox({
	lockdownRequireManagedGuestSession,
	checked,
	onChange,
}: {
	lockdownRequireManagedGuestSession: boolean;
	checked: boolean;
	onChange: (value: boolean) => void;
}) {
	return (
		<LabeledCheckbox
			checked={checked}
			onChange={onChange}
			header={i18nPackage.t("editQuizPage.platformStep.chromeOs.title")}
			hint={
				lockdownRequireManagedGuestSession
					? i18nPackage.t("editQuizPage.platformStep.chromeOs.hintManagedGuestSession")
					: i18nPackage.t("editQuizPage.platformStep.chromeOs.hintUnrestricted")
			}
		>
			{i18nPackage.t("editQuizPage.platformStep.chromeOs.description")}
		</LabeledCheckbox>
	);
}
